/**
 * 筛选器配置面板 Reducer
 * 管理筛选器配置状态的所有变更
 */

import type {
  IFilterState,
  FilterAction,
  IColumnInfo,
  IDefaultValue,
  IDateDefaultValue,
  IStringDefaultValue,
  INumberDefaultValue,
  IInitialFilterData,
  TScopeType,
} from './types';

interface ILegacyFilterData extends IInitialFilterData {
  defaultType?: string;
  timeType?: string;
  lastCount?: number;
  last_day?: string;
  defaultCompareTimes?: Array<{
    type?: 'year' | 'month' | 'week' | 'day';
    offset?: number;
  }>;
  isFull?: boolean;
  defaultOperation?: string;
  operation?: string;
  defaultFieldFilters?: any[];
}

/**
 * 初始状态
 */
export const initFilterState = (): IFilterState => ({
  dataset: null,
  column: null,
  filterType: '',
  filterName: '',
  columnInfo: null,
  defaultValue: {},
  scope: {
    scopeType: '' as TScopeType,
    scopeCharts: [],
  },
  relevantFilters: [],
  validation: {},
  ui: {
    dateTypeValidating: false,
    dateTypeDisabled: false,
    showDefaultValue: false,
    showScope: true,
    showRelevant: false,
  },
});

/**
 * 根据字段类型自动填充筛选器类型
 */
const getAutoFilterType = (columnType: string): 'date' | 'string' | '' => {
  if (columnType === 'date') {
    return 'date';
  }
  if (['string', 'number', 'float', 'percent'].includes(columnType)) {
    return 'string';
  }
  return '';
};

/**
 * 根据字段信息自动填充筛选器名称
 */
const getAutoFilterName = (columnInfo: IColumnInfo, isEn: boolean): string => {
  if (isEn && columnInfo.displayNameEn) {
    return columnInfo.displayNameEn;
  }
  if (columnInfo.displayName) {
    return columnInfo.displayName;
  }
  return columnInfo.name || '';
};

const normalizeBoolean = (value: any, defaultValue: boolean): boolean => {
  if (value === undefined || value === null) return defaultValue;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase();
    if (v === 'false' || v === '0' || v === 'no') return false;
    if (v === 'true' || v === '1' || v === 'yes') return true;
  }
  return Boolean(value);
};

/**
 * 构造日期类型的默认值
 * 规则：默认使用「最近7天」的动态时间，便于默认展示人类可读的文案
 */
const getDefaultDateValue = (): IDateDefaultValue => ({
  mode: 'dynamic',
  defaultType: 'day',
  dynamicValue: {
    granularity: 'day',
    offset: 0,
    count: 7,
    code: '7-day',
  },
});

/**
 * 构造字符串类型的默认值（非数值字段）
 * 规则：默认视为「全选」，内部 value 为空数组，由 isFull 标记含义
 * 与老版 Filter.tsx 中字符串筛选器的初始化逻辑保持一致
 */
const getDefaultStringValue = (): IStringDefaultValue => ({
  value: [],
  isFull: true,
  operation: 'include',
});

/**
 * 验证 Scope 配置是否需要重置
 * 当字段变更时，检查 Scope 配置是否还符合逻辑
 */
const validateScopeCharts = (
  scopeCharts: IFilterState['scope']['scopeCharts'],
  newColumnInfo: IColumnInfo
): IFilterState['scope']['scopeCharts'] => {
  return scopeCharts.map(chart => {
    // 如果图表有关联字段，检查类型兼容性
    if (chart.applyToColumn && chart.applyToColumn.length > 0) {
      const hasCompatibleField = chart.applyToColumn.some(field => {
        return isFieldTypeCompatible(newColumnInfo.columnType, field.columnType);
      });
      
      if (!hasCompatibleField) {
        // 没有兼容字段，清空关联字段配置
        return {
          ...chart,
          applyToColumn: undefined,
          error: 'type_mismatch' as const,
        };
      }
    }
    
    return chart;
  });
};

/**
 * 检查字段类型兼容性
 */
const isFieldTypeCompatible = (filterType: string, chartType: string): boolean => {
  // 字符串或日期：必须完全一致
  if (filterType === 'string' || filterType === 'date') {
    return filterType === chartType;
  }
  
  // 数值类型：number、float、percent 之间可以兼容
  const numericTypes = ['number', 'float', 'percent'];
  if (numericTypes.includes(filterType)) {
    return numericTypes.includes(chartType);
  }
  
  return false;
};

/**
 * 过滤级联子筛选器配置（只保留同一数据表的筛选器）
 */
const filterRelevantFilters = (
  relevantFilters: Array<{ i: string; filterName: string; }>,
  existingFilters: Array<{ i: string; dataset: string; }>,
  newDataset: string | null
): Array<{ i: string; filterName: string; }> => {
  if (!newDataset) {
    return [];
  }
  
  return relevantFilters.filter(item => {
    const filter = existingFilters.find(f => f.i === item.i);
    return filter?.dataset === newDataset;
  });
};

/**
 * Filter Reducer
 */
export const filterReducer = (
  state: IFilterState,
  action: FilterAction,
  props?: {
    existingFilters?: Array<{ i: string; dataset: string; }>;
    isEn?: boolean;
  }
): IFilterState => {
  const { existingFilters = [], isEn = false } = props || {};

  switch (action.type) {
    case 'SET_DATASET': {
      const newDataset = action.payload;
      
      return {
        ...state,
        dataset: newDataset,
        // 清空字段相关状态
        column: null,
        filterType: '',
        filterName: '',
        columnInfo: null,
        // 清空默认值
        defaultValue: {},
        // 重置 Scope
        scope: {
          scopeType: '',
          scopeCharts: [],
        },
        // 过滤级联子筛选器配置（只保留同一数据表的筛选器）
        relevantFilters: filterRelevantFilters(
          state.relevantFilters,
          existingFilters,
          newDataset
        ),
        // 重置校验状态
        validation: {},
        // 更新 UI 状态
        ui: {
          ...state.ui,
          // 数据表变更时，清理掉日期类型校验的残留状态
          dateTypeValidating: false,
          dateTypeDisabled: false,
          showDefaultValue: false,
          showScope: false,
          showRelevant: false,
        },
      };
    }

    case 'SET_COLUMN': {
      const { column, columnInfo } = action.payload;
      
      // 自动填充类型和名称
      const autoFilterType = getAutoFilterType(columnInfo.columnType);
      const autoFilterName = getAutoFilterName(columnInfo, isEn);

      // 根据字段类型初始化默认值
      // 规则：
      // - 日期字段：默认「最近7天」动态时间，便于默认展示文案
      // - 字符串字段（非数值）：默认视为「全选」
      // - 数值字段：交由数值筛选器组件处理，此处不预填默认值
      let defaultValue: IDefaultValue = {};
      if (autoFilterType === 'date') {
        defaultValue = getDefaultDateValue();
      } else if (autoFilterType === 'string' && !columnInfo.isNumeric) {
        defaultValue = getDefaultStringValue();
      }
      
      return {
        ...state,
        column,
        columnInfo,
        filterType: autoFilterType, // 自动填充类型
        filterName: autoFilterName, // 自动填充名称
        // 根据新字段类型重置默认值
        defaultValue,
        // 判断 Scope 是否需要重置
        scope: {
          ...state.scope,
          scopeCharts: validateScopeCharts(state.scope.scopeCharts, columnInfo),
        },
        // 重置字段相关校验状态
        validation: {
          ...state.validation,
          columnError: '',
        },
        // 更新 UI 状态
        ui: {
          ...state.ui,
          // 字段变更时，清理掉日期类型校验的残留状态
          dateTypeValidating: false,
          dateTypeDisabled: false,
          showDefaultValue: true, // 显示默认值配置
          showScope: true, // 显示 Scope 配置
          showRelevant: autoFilterType === 'string', // 字符串类型显示级联配置
        },
      };
    }

    case 'SET_FILTER_TYPE': {
      const newFilterType = action.payload;

      // 根据筛选器类型重置默认值
      // 场景：
      // - 从字符串手动切换为日期时，构造一个合理的默认日期值（最近7天）
      // - 从日期切回字符串时，如果是非数值字段，恢复为「全选」的字符串默认值
      let defaultValue: IDefaultValue = {};
      if (newFilterType === 'date') {
        defaultValue = getDefaultDateValue();
      } else if (
        newFilterType === 'string' &&
       state.columnInfo &&
       !state.columnInfo.isNumeric
      ) {
        defaultValue = getDefaultStringValue();
      }

      return {
        ...state,
        filterType: newFilterType,
        // 根据类型重置默认值
        defaultValue,
        // 更新 UI 状态
        ui: {
          ...state.ui,
          showRelevant: newFilterType === 'string', // 字符串类型显示级联配置
        },
      };
    }

    case 'SET_FILTER_NAME': {
      return {
        ...state,
        filterName: action.payload,
        // 重置名称相关校验状态
        validation: {
          ...state.validation,
          nameError: '',
        },
      };
    }

    case 'SET_DEFAULT_VALUE': {
      const nextDefaultValue = Object.assign(
        {},
        state.defaultValue,
        action.payload
      );

      return {
        ...state,
        defaultValue: nextDefaultValue,
        // 重置默认值相关校验状态
        validation: {
          ...state.validation,
          defaultValueError: '',
        },
      };
    }

    case 'SET_SCOPE': {
      return {
        ...state,
        scope: {
          ...state.scope,
          ...action.payload,
        },
        // 重置 Scope 相关校验状态
        validation: {
          ...state.validation,
          scopeError: {},
        },
      };
    }

    case 'SET_RELEVANT_FILTERS': {
      return {
        ...state,
        relevantFilters: action.payload,
      };
    }

    case 'SET_VALIDATION': {
      return {
        ...state,
        validation: {
          ...state.validation,
          ...action.payload,
        },
      };
    }

    case 'SET_UI_STATE': {
      return {
        ...state,
        ui: {
          ...state.ui,
          ...action.payload,
        },
      };
    }

    case 'RESET_STATE': {
      return {
        ...initFilterState(),
        ...action.payload,
      };
    }

    case 'INIT_FROM_FILTER': {
      const initialData = action.payload;
      const legacyData = initialData as ILegacyFilterData;

      // 1. 归一化字段信息（columnInfo）
      // 旧版 otherInfo.chartId 可能为逗号拼接的「列表 id」或单图「字段归属 id」，需拆分语义：
      // - columnInfo.chartId / isCustomField：仅表示选中自定义字段的单图归属
      // - 多图 listChartId 由 EditFilter 初始化 effect 写入 ref.chartId，不在此赋值
      const columnInfo: IColumnInfo | null = initialData.otherInfo
        ? {
          columnType: initialData.otherInfo.columnType || '',
          columnDatasetId: initialData.otherInfo.columnDatasetId || '',
          isNumeric: ['number', 'float', 'percent'].includes(
            initialData.otherInfo.columnType || ''
          ),
          isCustomField: !!(
            initialData.otherInfo.sqlExpression ||
            initialData.otherInfo.customColumnChartId ||
            (initialData.otherInfo.chartId && !initialData.otherInfo.chartId.includes(','))
          ),
          chartId:
            initialData.otherInfo.customColumnChartId ||
            (initialData.otherInfo.chartId && !initialData.otherInfo.chartId.includes(',')
              ? initialData.otherInfo.chartId
              : undefined),
          sqlExpression: initialData.otherInfo.sqlExpression,
          name: initialData.column,
        }
        : null;

      // 2. 归一化筛选器类型（兼容旧数据中不存在于新枚举的类型，统一回退为 string）
      const rawFilterType = (initialData.filterType || '') as string;
      const supportedTypes = ['date', 'string', 'number'];
      const normalizedFilterType =
        rawFilterType && supportedTypes.includes(rawFilterType)
          ? (rawFilterType as IFilterState['filterType'])
          : (rawFilterType ? 'string' : '') as IFilterState['filterType'];

      // 3. 构建默认值配置（兼容旧版 Filter.tsx 的初始化逻辑）
      let defaultValue: IDefaultValue = {};

      if (normalizedFilterType === 'date') {
        // 日期类型默认值（兼容旧版 Filter.tsx 保存的结构）
        const legacyDefaultValue = initialData.defaultValue || [];
        const legacyDateType = legacyData.defaultType || legacyData.timeType || 'day';
        const legacyLastCount = legacyData.lastCount;
        const legacyFrom = legacyData.last_day;
        const legacyDefaultCompareTimes = legacyData.defaultCompareTimes;
        const dateDefault: IDateDefaultValue = {};

        // 通过默认值长度粗略判断是动态还是静态：
        // - 长度 > 1 视为静态区间
        // - 否则视为动态最近 N 天
        if (Array.isArray(legacyDefaultValue) && legacyDefaultValue.length > 1) {
          // 静态时间：defaultValue 形如 [start, end]
          dateDefault.mode = 'static';
          dateDefault.defaultType = legacyDateType as any;
          dateDefault.staticValue = {
            start: legacyDefaultValue[0],
            end: legacyDefaultValue[1],
          };
        } else {
          // 动态时间：defaultValue 形如 ['7-day'] 或为空（兜底为最近7天）
          const count = legacyLastCount || 7;
          const code =
            (Array.isArray(legacyDefaultValue) && legacyDefaultValue[0]) ||
            `${count}-${legacyDateType}`;
          dateDefault.mode = 'dynamic';
          dateDefault.defaultType = legacyDateType as any;
          dateDefault.dynamicValue = {
            granularity: legacyDateType as any,
            offset: 0,
            count,
            code,
          };
        }

        if (legacyFrom) {
          dateDefault.from = legacyFrom;
        }

        // 对比时间配置：旧版使用 defaultCompareTimes 数组
        if (Array.isArray(legacyDefaultCompareTimes) && legacyDefaultCompareTimes.length > 0) {
          const compare = legacyDefaultCompareTimes[0] || {};
          dateDefault.compareTimes = legacyDefaultCompareTimes;
          dateDefault.compareTime = {
            enabled: true,
            compareType: compare.type || 'day',
            offset: compare.offset || 0,
          };
        } else if (initialData.compareTime) {
          // 兼容新版可能直接保存的 compareTime 结构
          const compare = initialData.compareTime;
          dateDefault.compareTime = {
            enabled: !!compare.enabled,
            compareType: compare.compareType || 'day',
            offset: compare.offset || 0,
          };
        }
        defaultValue = dateDefault;
      } else if (normalizedFilterType === 'string') {
        // 字符串类型默认值
        const legacyIsFull = legacyData.isFull;
        const legacyDefaultValue = initialData.defaultValue || [];
        const legacyOperation = legacyData.defaultOperation || legacyData.operation || 'include';
        const legacyDefaultFieldFilters = legacyData.defaultFieldFilters;
        const stringDefault: IStringDefaultValue = {};

        // 与旧版逻辑一致：全选时内部值可以为空，由 isFull 标记含义
        stringDefault.value = legacyIsFull ? [] : (legacyDefaultValue as any);
        stringDefault.isFull = !!legacyIsFull;
        stringDefault.operation = legacyOperation as any;

        if (initialData.fieldTimeRange) {
          stringDefault.fieldTimeRange = initialData.fieldTimeRange;
        }

        // 兼容旧版的 defaultFieldFilters，用于字符串选项请求时的时间范围过滤
        if (Array.isArray(legacyDefaultFieldFilters) && legacyDefaultFieldFilters.length > 0) {
          stringDefault.filters = legacyDefaultFieldFilters;
        }
        defaultValue = stringDefault;
      } else if (normalizedFilterType === 'number') {
        // 数值类型默认值
        const legacyOperation = legacyData.defaultOperation || legacyData.operation || 'gt';
        const numberDefault: INumberDefaultValue = {
          operation: legacyOperation as any,
          value: (initialData.defaultValue || []) as any,
        };
        defaultValue = numberDefault;
      }

      // 4. 构建 Scope 配置
      const scope = initialData.scope
        ? {
          scopeType: (initialData.scope.scope_type || '') as IFilterState['scope']['scopeType'],
          scopeCharts: (initialData.scope.scope_charts || []).map((chart: any) => ({
            taskId: chart.task_id,
            name: chart.name || '',
            nameCn: chart.nameCn || chart.name_cn || '',
            columnDatasetId: chart.columnDatasetId,
            applyToColumn: chart.apply_to_column,
            // 兼容旧版 isDefaultDate 字段
            isDefaultDate: chart.isDefaultDate ?? chart.defaultDate,
          })),
        }
        : {
          scopeType: '' as TScopeType,
          scopeCharts: [],
        };

      return {
        ...initFilterState(),
        dataset: initialData.dataset || null,
        column: initialData.column || null,
        filterType: normalizedFilterType,
        filterName: initialData.filterName || '',
        columnInfo,
        defaultValue,
        scope,
        relevantFilters: Array.isArray(initialData.relevantFilters)
          ? initialData.relevantFilters
          : [],
        ui: {
          dateTypeValidating: false,
          dateTypeDisabled: false,
          showDefaultValue: !!initialData.column,
          showScope: normalizeBoolean(initialData.showScope, true),
          showRelevant: initialData.filterType === 'string',
        },
      };
    }

    default:
      return state;
  }
};

